import type { AABB, Vec2 } from '../../geo/types';
import type { GroundPiece } from '../../site/types';
import type { AtmosphereParams } from '../../render/types';
import { deltaE76, linearToLab, type Lab, type LinearRGB } from '../../core/color';
import { WORLD } from '../../core/config';
import { applyAtmosphere } from '../../render/atmosphere';

// Below this ΔE76 two fogged colours read as one; the blend into the far ring ends where the fog
// alone brings a piece's albedo that close to nearFields.
export const SEAM_FOG_DELTA_E = 2;
const RAMP_PRECISION_M = 1;
const ON_BOUNDS_M = 1e-6;

// A stretch of the bounds along which one ground piece meets the far ring, counter-clockwise, and the
// arc it spans along the bounds from their (minX, minY) corner.
export interface SeamRun {
  readonly piece: GroundPiece;
  readonly points: readonly Vec2[];
  readonly from: number;
  readonly to: number;
}

interface SeamEdge {
  readonly piece: GroundPiece;
  readonly from: number;
  readonly to: number;
  readonly a: Vec2;
  readonly b: Vec2;
}

function near(u: number, v: number): boolean {
  return Math.abs(u - v) <= ON_BOUNDS_M;
}

// Sides counter-clockwise from the (minX, minY) corner: bottom, right, top, left.
function sideHolding(a: Vec2, b: Vec2, bounds: AABB): number | null {
  if (near(a[1], bounds.minY) && near(b[1], bounds.minY)) return 0;
  if (near(a[0], bounds.maxX) && near(b[0], bounds.maxX)) return 1;
  if (near(a[1], bounds.maxY) && near(b[1], bounds.maxY)) return 2;
  if (near(a[0], bounds.minX) && near(b[0], bounds.minX)) return 3;
  return null;
}

function arcOn(side: number, x: number, y: number, bounds: AABB): number {
  const [width, height] = [bounds.maxX - bounds.minX, bounds.maxY - bounds.minY];
  if (side === 0) return x - bounds.minX;
  if (side === 1) return width + y - bounds.minY;
  if (side === 2) return width + height + bounds.maxX - x;
  return 2 * width + height + bounds.maxY - y;
}

function seamEdgesOf(piece: GroundPiece, bounds: AABB): SeamEdge[] {
  const ring = piece.polygon.outer;
  return ring.flatMap((a, i) => {
    const b = ring[(i + 1) % ring.length]!;
    const side = sideHolding(a, b, bounds);
    if (side === null) return [];
    const [from, to] = [arcOn(side, a[0], a[1], bounds), arcOn(side, b[0], b[1], bounds)];
    if (from === to) return [];
    return [from < to ? { piece, from, to, a, b } : { piece, from: to, to: from, a: b, b: a }];
  });
}

function extendRuns(runs: readonly SeamRun[], edge: SeamEdge): readonly SeamRun[] {
  const last = runs[runs.length - 1];
  if (last && last.piece === edge.piece && near(last.to, edge.from)) {
    return [...runs.slice(0, -1), { ...last, points: [...last.points, edge.b], to: edge.to }];
  }
  const run = { piece: edge.piece, points: [edge.a, edge.b], from: edge.from, to: edge.to };
  return [...runs, run];
}

// The site shares every seam vertex with the far ring, so the pieces' own edges along the bounds
// tell which piece each stretch of the far ring continues.
export function seamRuns(ground: readonly GroundPiece[], bounds: AABB): readonly SeamRun[] {
  const edges = ground.flatMap((piece) => seamEdgesOf(piece, bounds));
  return [...edges].sort((x, y) => x.from - y.from).reduce(extendRuns, []);
}

export function perimeterOf(bounds: AABB): number {
  return 2 * (bounds.maxX - bounds.minX + (bounds.maxY - bounds.minY));
}

// The far ring is swept out from the centre of the bounds (farRingSectors), so the edge between two
// sectors is a ray from that centre: every point on it has the arc where the ray leaves the bounds.
export function radialArcOf(p: Vec2, bounds: AABB): number {
  const halfWidth = (bounds.maxX - bounds.minX) / 2;
  const halfHeight = (bounds.maxY - bounds.minY) / 2;
  const dx = p[0] - bounds.minX - halfWidth;
  const dy = p[1] - bounds.minY - halfHeight;
  const across = Math.abs(dx) / halfWidth;
  const up = Math.abs(dy) / halfHeight;
  if (up >= across) {
    const x = bounds.minX + halfWidth + dx / up;
    return dy < 0 ? arcOn(0, x, bounds.minY, bounds) : arcOn(2, x, bounds.maxY, bounds);
  }
  const y = bounds.minY + halfHeight + dy / across;
  return dx > 0 ? arcOn(1, bounds.maxX, y, bounds) : arcOn(3, bounds.minX, y, bounds);
}

export function distanceBeyond(p: Vec2, bounds: AABB): number {
  const dx = Math.max(bounds.minX - p[0], 0, p[0] - bounds.maxX);
  const dy = Math.max(bounds.minY - p[1], 0, p[1] - bounds.maxY);
  return Math.hypot(dx, dy);
}

function foggedLab(colour: LinearRGB, distance: number, atmosphere: AtmosphereParams): Lab {
  return linearToLab(applyAtmosphere(atmosphere, colour, distance, WORLD.groundY));
}

// The painter view cannot set this distance: its warp holds every far-ring fragment 4-5.5 km from
// the camera, where fogged polderFields still lies 5-6 ΔE from fogged nearFields. So the blend is
// measured for a viewer standing on the seam, who sees the fog close the gap by this distance.
export function rampDistance(
  albedo: LinearRGB,
  base: LinearRGB,
  atmosphere: AtmosphereParams
): number {
  const gap = (d: number) =>
    deltaE76(foggedLab(albedo, d, atmosphere), foggedLab(base, d, atmosphere));
  if (gap(0) <= SEAM_FOG_DELTA_E) return 0;
  let inside = 0;
  let outside: number = WORLD.farRingRadius;
  if (gap(outside) > SEAM_FOG_DELTA_E) return outside;
  while (outside - inside > RAMP_PRECISION_M) {
    const middle = (inside + outside) / 2;
    if (gap(middle) > SEAM_FOG_DELTA_E) inside = middle;
    else outside = middle;
  }
  return outside;
}

// How far from its albedo towards nearFields the ramp has turned a colour this far beyond the bounds.
export function rampWeight(distance: number, beyond: number): number {
  const t = distance > 0 ? Math.min(1, Math.max(0, beyond / distance)) : 1;
  return t * t * (3 - 2 * t);
}

export function rampColour(
  albedo: LinearRGB,
  base: LinearRGB,
  distance: number,
  beyond: number
): LinearRGB {
  const w = rampWeight(distance, beyond);
  return [
    albedo[0] * (1 - w) + base[0] * w,
    albedo[1] * (1 - w) + base[1] * w,
    albedo[2] * (1 - w) + base[2] * w,
  ];
}
